/**
 * GARDEN-137: Structured Analytics Tracking
 * Google Analytics 4 integration with privacy compliance
 */

// Note: Using fetch directly for error reporting to avoid circular dependencies
// apiService is available via api.ts but using fetch keeps analytics decoupled

/** GA4 Measurement ID - set via VITE_GA4_MEASUREMENT_ID env var */
const GA4_MEASUREMENT_ID = import.meta.env.VITE_GA4_MEASUREMENT_ID || '';

// Analytics event types
export type AnalyticsEvent =
  | 'page_view'
  | 'garden_stage_progression'
  | 'pet_interaction'
  | 'theme_change'
  | 'feature_usage'
  | 'user_engagement'
  | 'error';

interface ErrorReport {
  message: string;
  stack?: string;
  url: string;
  userAgent: string;
  timestamp: string;
  userId?: string;
  guildId?: string;
  metadata?: Record<string, unknown>;
}

interface PerformanceMetrics {
  pageLoadTime: number;
  domContentLoaded: number;
  firstPaint?: number;
  firstContentfulPaint?: number;
}

// GA4 Event parameters
interface GA4EventParams {
  [key: string]: string | number | boolean | undefined;
}

// GARDEN-137: Custom event payloads
type GardenStageEvent = {
  event: 'garden_stage_progression';
  params: {
    previous_stage: number;
    new_stage: number;
    total_points: number;
    guild_id?: string;
  };
};

type PetInteractionEvent = {
  event: 'pet_interaction';
  params: {
    interaction_type: 'feed' | 'adopt' | 'wander' | 'unlock';
    pet_type?: string | null;
    pet_variant?: string | null;
    streak_days?: number;
    guild_id?: string;
  };
};

type ThemeChangeEvent = {
  event: 'theme_change';
  params: {
    theme_id: string;
    previous_theme?: string;
    guild_id?: string;
  };
};

type FeatureUsageEvent = {
  event: 'feature_usage';
  params: {
    feature: 'screenshot' | 'focus_me' | 'avatar_customization' | 'drawer_open' | 'onboarding_complete' | 'social_share';
    guild_id?: string;
  };
};

type UserEngagementEvent = {
  event: 'user_engagement';
  params: {
    engagement_type: 'tile_water' | 'tile_plant' | 'reply' | 'activity_perform' | 'rank_up';
    points_delta?: number;
    guild_id?: string;
  };
};

export type CustomAnalyticsEvent =
  | GardenStageEvent
  | PetInteractionEvent
  | ThemeChangeEvent
  | FeatureUsageEvent
  | UserEngagementEvent;

class Analytics {
  private userId: string | null = null;
  private guildId: string | null = null;
  private errorQueue: ErrorReport[] = [];
  private flushInterval: number | null = null;
  private readonly FLUSH_INTERVAL_MS = 30000; // GARDEN-137: 30s batching as per spec
  private readonly MAX_QUEUE_SIZE = 50;
  private ga4Enabled = false;
  private dntRespected = false;
  private consentGranted = false;
  private eventQueue: CustomAnalyticsEvent[] = [];
  private genericEventQueue: Array<{ event: string; properties: Record<string, unknown> }> = [];
  private sessionStartTime: number = Date.now();
  private lastActivityTime: number = Date.now();
  private dailyReturnTracked = false;
  // GARDEN-210: Store bound event handlers for proper cleanup
  private boundBeforeUnloadHandler: (() => void) | null = null;
  private boundVisibilityHandler: (() => void) | null = null;

  constructor() {
    // Note: Global error handlers are set up by error-tracking.ts (GARDEN-130)
    // to avoid duplicate reporting. Analytics focuses on GA4 events/metrics only.
    this.startFlushInterval();
    this.initializeGA4();
    this.trackSessionStart();
    this.checkDailyReturn();
  }

  /**
   * GARDEN-137: Initialize GA4 with privacy checks
   */
  private initializeGA4(): void {
    // Check for Do Not Track
    this.dntRespected = this.isDoNotTrackEnabled();
    
    // Check for consent (GDPR/CCPA ready)
    this.consentGranted = this.hasConsent();
    
    // Only enable GA4 if not DNT, consent granted, and measurement ID is configured
    this.ga4Enabled = !this.dntRespected && this.consentGranted && typeof window !== 'undefined' && !!GA4_MEASUREMENT_ID;
    
    if (this.dntRespected) {
      if (import.meta.env.DEV) {
        console.log('[Analytics] Do Not Track enabled - GA4 disabled');
      }
    }
    
    if (!this.consentGranted) {
      if (import.meta.env.DEV) {
        console.log('[Analytics] Consent not granted - GA4 in consent mode');
      }
    }
    
    // Send queued events once initialized
    this.flushEventQueue();
  }

  /**
   * GARDEN-137: Check if Do Not Track is enabled
   */
  private isDoNotTrackEnabled(): boolean {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return false;
    }
    
    // Check standard DNT
    const dnt = navigator.doNotTrack || (window as any).doNotTrack;
    if (dnt === '1' || dnt === 'yes') {
      return true;
    }
    
    // Check global privacy control
    const gpc = (navigator as any).globalPrivacyControl;
    if (gpc === true) {
      return true;
    }
    
    return false;
  }

  /**
   * GARDEN-137: Check for user consent (placeholder for consent management)
   */
  private hasConsent(): boolean {
    // Check for stored consent preference
    try {
      const consent = localStorage.getItem('garden_analytics_consent');
      if (consent === 'granted') return true;
      if (consent === 'denied') return false;
    } catch {
      // LocalStorage not available
    }
    
    // Default to true for existing users (soft opt-in)
    // New users should go through explicit consent flow
    return true;
  }

  /**
   * GARDEN-137: Set user consent preference
   */
  setConsent(granted: boolean): void {
    this.consentGranted = granted;
    this.ga4Enabled = !this.dntRespected && granted;
    
    try {
      localStorage.setItem('garden_analytics_consent', granted ? 'granted' : 'denied');
    } catch {
      // LocalStorage not available
    }
    
    // Update gtag consent mode if available
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('consent', 'update', {
        analytics_storage: granted ? 'granted' : 'denied',
        ad_storage: 'denied', // Never enable ad storage
        ad_user_data: 'denied',
        ad_personalization: 'denied',
      });
    }
    
    if (granted) {
      this.flushEventQueue();
    }
  }

  /**
   * GARDEN-137: Track page view in GA4
   */
  trackPageView(pagePath?: string, pageTitle?: string): void {
    if (!this.ga4Enabled) {
      return;
    }
    
    const gtag = (window as any).gtag;
    if (!gtag) return;
    
    const params: GA4EventParams = {
      page_location: window.location.href,
      page_path: pagePath || window.location.pathname + window.location.hash,
      page_title: pageTitle || document.title,
      send_to: GA4_MEASUREMENT_ID,
    };
    
    if (this.userId) {
      params.user_id = this.userId;
    }
    
    gtag('event', 'page_view', params);
  }

  /**
   * GARDEN-137: Track custom events
   */
  trackCustomEvent(eventData: CustomAnalyticsEvent): void {
    // Queue if analytics not ready
    if (!this.ga4Enabled) {
      this.eventQueue.push(eventData);
      return;
    }
    
    const gtag = (window as any).gtag;
    if (!gtag) return;
    
    const params: GA4EventParams = {
      ...eventData.params,
      send_to: GA4_MEASUREMENT_ID,
    };
    
    // Add guild_id context if available
    if (this.guildId && !params.guild_id) {
      params.guild_id = this.guildId;
    }
    
    gtag('event', eventData.event, params);
  }

  /**
   * GARDEN-137: Flush queued events
   */
  private flushEventQueue(): void {
    if (!this.ga4Enabled || this.eventQueue.length === 0) return;
    
    const events = [...this.eventQueue];
    this.eventQueue = [];
    
    events.forEach(event => this.trackCustomEvent(event));
  }

  /**
   * GARDEN-137: Generic track method - accepts any event name and properties
   * No PII should be included in properties
   */
  track(event: string, properties?: Record<string, unknown>): void {
    if (this.dntRespected) return;

    // Sanitize properties to ensure no PII
    const sanitizedProps = this.sanitizeProperties(properties || {});

    // Add context
    const enrichedProps = {
      ...sanitizedProps,
      guild_id: this.guildId,
      timestamp: Date.now(),
    };

    // Send to GA4 if enabled
    if (this.ga4Enabled) {
      const gtag = (window as any).gtag;
      if (gtag) {
        gtag('event', event, {
          ...enrichedProps,
          send_to: GA4_MEASUREMENT_ID,
        });
      }
    }

    // Queue for batching
    this.genericEventQueue.push({ event, properties: enrichedProps });

    // Update activity timestamp for session tracking
    this.lastActivityTime = Date.now();

    // Flush if queue is full
    if (this.genericEventQueue.length >= this.MAX_QUEUE_SIZE) {
      this.flushGenericEvents();
    }
  }

  /**
   * GARDEN-137: Sanitize properties to remove potential PII
   */
  private sanitizeProperties(props: Record<string, unknown>): Record<string, unknown> {
    const sanitized: Record<string, unknown> = {};
    const piiPatterns = [
      /email/i,
      /phone/i,
      /address/i,
      /ssn/i,
      /password/i,
      /token/i,
      /credit/i,
      /card/i,
      /name/i, // Remove personal names but preserve IDs
    ];

    for (const [key, value] of Object.entries(props)) {
      // Skip keys that look like PII
      if (piiPatterns.some(pattern => pattern.test(key))) {
        continue;
      }

      // Only include primitive types and arrays of primitives
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        sanitized[key] = value;
      } else if (Array.isArray(value)) {
        sanitized[key] = value.filter(v => typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean');
      }
    }

    return sanitized;
  }

  /**
   * GARDEN-137: Track session start
   */
  private trackSessionStart(): void {
    this.sessionStartTime = Date.now();
    this.track('session_start', {
      referrer: document.referrer || undefined,
      user_agent_hint: this.getUserAgentHint(),
    });
  }

  /**
   * GARDEN-137: Get non-identifying user agent hint
   */
  private getUserAgentHint(): string {
    const ua = navigator.userAgent.toLowerCase();
    if (ua.includes('mobile')) return 'mobile';
    if (ua.includes('tablet')) return 'tablet';
    return 'desktop';
  }

  /**
   * GARDEN-137: Check for daily return user
   */
  private checkDailyReturn(): void {
    if (this.dntRespected) return;

    try {
      const lastVisitKey = 'garden_last_visit';
      const lastVisit = localStorage.getItem(lastVisitKey);
      const now = new Date();
      const todayKey = `${now.getFullYear()}-${now.getMonth()}-${now.getDate()}`;

      if (lastVisit) {
        const lastDate = new Date(lastVisit);
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayKey = `${yesterday.getFullYear()}-${yesterday.getMonth()}-${yesterday.getDate()}`;

        // Check if last visit was yesterday (consecutive day)
        const lastVisitDateKey = `${lastDate.getFullYear()}-${lastDate.getMonth()}-${lastDate.getDate()}`;

        if (lastVisitDateKey === yesterdayKey) {
          // Consecutive day return
          const streak = parseInt(localStorage.getItem('garden_visit_streak') || '0', 10);
          const newStreak = streak + 1;
          localStorage.setItem('garden_visit_streak', String(newStreak));
          this.track('daily_return', { consecutive: true, streak_days: newStreak });
        } else if (lastVisitDateKey !== todayKey) {
          // Returned after a gap
          localStorage.setItem('garden_visit_streak', '1');
          this.track('daily_return', { consecutive: false, streak_days: 1 });
        }
      } else {
        // First visit ever
        localStorage.setItem('garden_visit_streak', '1');
        this.track('first_visit', {});
      }

      localStorage.setItem(lastVisitKey, now.toISOString());
      this.dailyReturnTracked = true;
    } catch {
      // localStorage not available
    }
  }

  /**
   * GARDEN-137: Track session duration on page unload
   */
  trackSessionEnd(): void {
    const duration = Date.now() - this.sessionStartTime;
    const activeTime = this.lastActivityTime - this.sessionStartTime;

    this.track('session_end', {
      duration_ms: duration,
      active_time_ms: Math.max(0, activeTime),
    });

    // Flush immediately on session end
    this.flushGenericEvents();
  }

  /**
   * GARDEN-137: Convenience methods for specific events
   */
  trackGardenStageProgression(previousStage: number, newStage: number, totalPoints: number, guildId?: string): void {
    this.trackCustomEvent({
      event: 'garden_stage_progression',
      params: {
        previous_stage: previousStage,
        new_stage: newStage,
        total_points: totalPoints,
        guild_id: guildId || this.guildId || undefined,
      },
    });
  }

  trackPetInteraction(
    interactionType: PetInteractionEvent['params']['interaction_type'],
    options?: {
      petType?: string | null;
      petVariant?: string | null;
      streakDays?: number;
      guildId?: string;
    }
  ): void {
    this.trackCustomEvent({
      event: 'pet_interaction',
      params: {
        interaction_type: interactionType,
        pet_type: options?.petType,
        pet_variant: options?.petVariant,
        streak_days: options?.streakDays,
        guild_id: options?.guildId || this.guildId || undefined,
      },
    });
  }

  trackThemeChange(themeId: string, previousTheme?: string, guildId?: string): void {
    this.trackCustomEvent({
      event: 'theme_change',
      params: {
        theme_id: themeId,
        previous_theme: previousTheme,
        guild_id: guildId || this.guildId || undefined,
      },
    });
  }

  trackFeatureUsage(feature: FeatureUsageEvent['params']['feature'], guildId?: string): void {
    this.trackCustomEvent({
      event: 'feature_usage',
      params: {
        feature,
        guild_id: guildId || this.guildId || undefined,
      },
    });
  }

  trackUserEngagement(
    engagementType: UserEngagementEvent['params']['engagement_type'],
    options?: { pointsDelta?: number; guildId?: string }
  ): void {
    this.trackCustomEvent({
      event: 'user_engagement',
      params: {
        engagement_type: engagementType,
        points_delta: options?.pointsDelta,
        guild_id: options?.guildId || this.guildId || undefined,
      },
    });
  }

  /**
   * Set user context for error reports
   */
  setUser(userId: string): void {
    this.userId = userId;

    // Update GA4 user ID
    if (this.ga4Enabled && typeof window !== 'undefined' && (window as any).gtag && GA4_MEASUREMENT_ID) {
      (window as any).gtag('config', GA4_MEASUREMENT_ID, {
        user_id: userId,
      });
    }
  }

  /**
   * Set guild context
   */
  setGuild(guildId: string): void {
    this.guildId = guildId;
  }

  /**
   * Clear user context
   */
  clearUser(): void {
    this.userId = null;
  }

  /**
   * Report an error to the server and GA4
   */
  reportError(
    error: Error | string,
    metadata?: Record<string, unknown>,
    immediate = false
  ): void {
    const errorReport: ErrorReport = {
      message: error instanceof Error ? error.message : error,
      stack: error instanceof Error ? error.stack : undefined,
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      userId: this.userId || undefined,
      guildId: this.guildId || undefined,
      metadata,
    };

    // Also track in GA4 if enabled
    if (this.ga4Enabled) {
      const gtag = (window as any).gtag;
      if (gtag) {
        gtag('event', 'exception', {
          description: errorReport.message.substring(0, 150),
          fatal: metadata?.fatal === true,
          send_to: GA4_MEASUREMENT_ID,
        });
      }
    }

    if (immediate) {
      this.sendError(errorReport);
    } else {
      this.errorQueue.push(errorReport);
      
      if (this.errorQueue.length >= this.MAX_QUEUE_SIZE) {
        this.flushErrors();
      }
    }
  }

  /**
   * Collect performance metrics
   */
  collectPerformanceMetrics(): PerformanceMetrics | null {
    if (typeof window === 'undefined' || !window.performance) {
      return null;
    }

    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    const paint = performance.getEntriesByType('paint');

    if (!navigation) {
      return null;
    }

    const metrics: PerformanceMetrics = {
      pageLoadTime: navigation.loadEventEnd - navigation.startTime,
      domContentLoaded: navigation.domContentLoadedEventEnd - navigation.startTime,
    };

    const firstPaint = paint.find(p => p.name === 'first-paint');
    const firstContentfulPaint = paint.find(p => p.name === 'first-contentful-paint');

    if (firstPaint) {
      metrics.firstPaint = firstPaint.startTime;
    }

    if (firstContentfulPaint) {
      metrics.firstContentfulPaint = firstContentfulPaint.startTime;
    }

    return metrics;
  }

  /**
   * Send error to server endpoint
   */
  private async sendError(errorReport: ErrorReport): Promise<void> {
    try {
      const baseUrl = typeof window !== 'undefined' ? `${window.location.protocol}//${window.location.host}` : '';
      await fetch(`${baseUrl}/api/log-error`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(errorReport),
        keepalive: true // Use keepalive for beacon-like behavior
      });
    } catch (e) {
      // Silently fail in production - analytics shouldn't break the app
    }
  }

  /**
   * Flush error queue to server
   */
  private flushErrors(): void {
    if (this.errorQueue.length === 0) return;

    const errors = [...this.errorQueue];
    this.errorQueue = [];

    Promise.all(errors.map(e => this.sendError(e))).catch(() => {
      // Silently fail - analytics shouldn't break the app
    });
  }

  /**
   * Note: Global error handlers are centralized in error-tracking.ts (GARDEN-130)
   * to prevent duplicate error reporting. Analytics.ts focuses on GA4 event tracking.
   * Use reportError() directly for manual error tracking when needed.
   */

  /**
   * GARDEN-137: Flush generic events queue
   */
  private flushGenericEvents(): void {
    if (this.genericEventQueue.length === 0) return;

    const events = [...this.genericEventQueue];
    this.genericEventQueue = [];

    // Send to server endpoint for batch processing
    try {
      const baseUrl = typeof window !== 'undefined' ? `${window.location.protocol}//${window.location.host}` : '';
      fetch(`${baseUrl}/api/analytics/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ events }),
        keepalive: true,
      }).catch(() => {
        // Silently fail - analytics shouldn't break the app
      });
    } catch {
      // Fail silently
    }
  }

  /**
   * Start interval to flush events
   */
  private startFlushInterval(): void {
    this.flushInterval = window.setInterval(() => {
      this.flushErrors();
      this.flushEventQueue();
      this.flushGenericEvents();
    }, this.FLUSH_INTERVAL_MS);

    // GARDEN-210: Store bound handlers for proper cleanup
    this.boundBeforeUnloadHandler = () => {
      this.trackSessionEnd();
      this.flush();
    };

    this.boundVisibilityHandler = () => {
      if (document.visibilityState === 'hidden') {
        this.trackSessionEnd();
        this.flush();
      }
    };

    // Flush on page unload
    window.addEventListener('beforeunload', this.boundBeforeUnloadHandler);

    // Also track session end on visibility change (mobile app switch)
    document.addEventListener('visibilitychange', this.boundVisibilityHandler);
  }

  /**
   * Flush all queued data
   */
  private flush(): void {
    this.flushErrors();
    this.flushEventQueue();
    this.flushGenericEvents();
  }

  /**
   * Cleanup - flush remaining errors and clear interval
   */
  dispose(): void {
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
      this.flushInterval = null;
    }

    // GARDEN-210: Remove event listeners to prevent memory leaks
    if (this.boundBeforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.boundBeforeUnloadHandler);
      this.boundBeforeUnloadHandler = null;
    }
    if (this.boundVisibilityHandler) {
      document.removeEventListener('visibilitychange', this.boundVisibilityHandler);
      this.boundVisibilityHandler = null;
    }

    this.trackSessionEnd();
    this.flush();
  }
}

// Singleton instance
export const analytics = new Analytics();

/**
 * Helper function to wrap async functions with error tracking
 */
export function trackAsync<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  name: string
): T {
  return (async (...args: any[]) => {
    try {
      return await fn(...args);
    } catch (error) {
      analytics.reportError(
        error instanceof Error ? error : new Error(String(error)),
        { functionName: name, args }
      );
      throw error;
    }
  }) as T;
}
